import { useEffect, useRef, useState } from 'react';

const MIN_DRAG_DISTANCE = 3;
const ITEM_SELECTOR = '[data-timeline-item-id]';
export const RUBBER_BAND_ACTIVE_ATTR = 'data-rubber-band-active';

type Rect = { left: number; top: number; width: number; height: number };

const setRubberBandActive = (active: boolean) => {
  if (active) {
    document.body.setAttribute(RUBBER_BAND_ACTIVE_ATTR, '');
    document.body.style.userSelect = 'none';
  } else {
    document.body.removeAttribute(RUBBER_BAND_ACTIVE_ATTR);
    document.body.style.userSelect = '';
  }
};

const getRectFromPoints = (a: { x: number; y: number }, b: { x: number; y: number }): Rect => ({
  left: Math.min(a.x, b.x),
  top: Math.min(a.y, b.y),
  width: Math.abs(a.x - b.x),
  height: Math.abs(a.y - b.y),
});

const rectsIntersect = (
  a: Rect,
  b: { left: number; top: number; right: number; bottom: number },
): boolean =>
  a.left < b.right && a.left + a.width > b.left && a.top < b.bottom && a.top + a.height > b.top;

const isDragSurface = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest(ITEM_SELECTOR)) return false;
  if (target.closest('[data-no-rubber-band]')) return false;
  return true;
};

const hitTestItems = (container: HTMLElement, selectionRect: Rect): number[] => {
  const itemEls = container.querySelectorAll<HTMLElement>(ITEM_SELECTOR);
  const hitIds: number[] = [];
  for (const el of itemEls) {
    const bounds = el.getBoundingClientRect();
    if (rectsIntersect(selectionRect, bounds)) {
      const id = Number(el.dataset.timelineItemId);
      if (!Number.isNaN(id)) hitIds.push(id);
    }
  }
  return hitIds;
};

export const TimelineRubberBand = ({
  scrollContainerRef,
  onSelect,
  onClearSelection,
  onPreviewChange,
}: {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onSelect: (itemIds: number[], additive: boolean) => void;
  onClearSelection: () => void;
  onPreviewChange?: (previewIds: ReadonlySet<number> | null) => void;
}) => {
  const startRef = useRef<{ x: number; y: number; additive: boolean } | null>(null);
  const draggingRef = useRef(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const onPreviewChangeRef = useRef(onPreviewChange);
  onPreviewChangeRef.current = onPreviewChange;

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!isDragSurface(e.target)) return;
      startRef.current = {
        x: e.clientX,
        y: e.clientY,
        additive: e.shiftKey || e.metaKey || e.ctrlKey,
      };
      draggingRef.current = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!draggingRef.current && Math.hypot(dx, dy) < MIN_DRAG_DISTANCE) return;
      if (!draggingRef.current) setRubberBandActive(true);
      draggingRef.current = true;
      const nextRect = getRectFromPoints(start, { x: e.clientX, y: e.clientY });
      setRect(nextRect);
      onPreviewChangeRef.current?.(new Set(hitTestItems(container, nextRect)));
    };

    const onPointerUp = (e: PointerEvent) => {
      const start = startRef.current;
      const dragged = draggingRef.current;
      startRef.current = null;
      draggingRef.current = false;
      setRubberBandActive(false);
      onPreviewChangeRef.current?.(null);

      if (!start) return;

      if (!dragged) {
        if (!start.additive) onClearSelection();
        return;
      }

      const hitIds = hitTestItems(
        container,
        getRectFromPoints(start, { x: e.clientX, y: e.clientY }),
      );
      setRect(null);
      onSelect(hitIds, start.additive);
    };

    const cancelDrag = () => {
      startRef.current = null;
      draggingRef.current = false;
      setRubberBandActive(false);
      onPreviewChangeRef.current?.(null);
      setRect(null);
    };

    const onVisibilityChange = () => {
      if (document.hidden) cancelDrag();
    };

    container.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', cancelDrag);
    window.addEventListener('blur', cancelDrag);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', cancelDrag);
      window.removeEventListener('blur', cancelDrag);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      setRubberBandActive(false);
      onPreviewChangeRef.current?.(null);
    };
  }, [scrollContainerRef, onSelect, onClearSelection]);

  if (!rect) return null;

  return (
    <div
      className="fixed pointer-events-none border border-border-info bg-bg-info-subtle/30 z-50"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
    />
  );
};
